import { useMemo, useState } from "react";
import { useCustom, useGetLocale, useTranslate } from "@refinedev/core";
import {
  SelectField,
  StatusBadge,
  type Column,
  type StatusTone,
} from "@fawkes/ui";

import { formatDate, formatEuro } from "../i18n/format";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

// Crematorium (Epic 21): the Crematorium invoices list — the React port of the Jmix
// `FacturenListView` (read-only). Columns: Factuurnummer / Factuurdatum / Vervaldatum / Betaler /
// Status (payment-status badge) / Totaal / Openstaand, newest invoice date first. Client-side search
// (factuurnummer + betaler) and a payment-status filter (the crematorium invoice volume is modest).
// The Jmix view stays reachable by its @Route (strangler-fig).
//
// GET uses a RELATIVE url (the dataProvider prepends /api once). ROLE_CREMATORIUM_VIEW is
// server-enforced; a 403 surfaces inline.

interface FactuurRow {
  id: string;
  dossierId: string | null;
  factuurNummer: string | null;
  factuurDatum: string | null;
  vervaldatum: string | null;
  betalerNaam: string | null;
  betaalStatus: string;
  totaalBedrag: number | null;
  openstaandBedrag: number | null;
}

// Payment-status vocabulary (stable enum ids) → badge tone.
const STATUSSEN = [
  "NOG_NIET_GEFACTUREERD",
  "GEFACTUREERD",
  "BETAALLINK_VERSTUURD",
  "DEELS_BETAALD",
  "BETAALD",
  "VERVALLEN",
  "HERINNERING_VERSTUURD",
  "GECREDITEERD",
] as const;

const STATUS_TONE: Record<string, StatusTone> = {
  BETAALD: "success",
  DEELS_BETAALD: "warning",
  HERINNERING_VERSTUURD: "warning",
  VERVALLEN: "danger",
};
const tone = (status: string): StatusTone => STATUS_TONE[status] ?? "neutral";

export function CrematoriumFacturenList() {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";

  const { data, isLoading, isError } = useCustom<FactuurRow[]>({
    url: "crematorium/facturen",
    method: "get",
  });
  const rows = data?.data ?? [];

  const [zoek, setZoek] = useState("");
  const [status, setStatus] = useState("");

  const filtered = useMemo(() => {
    const q = zoek.trim().toLowerCase();
    return rows.filter((r) => {
      if (status && r.betaalStatus !== status) return false;
      if (!q) return true;
      return (
        (r.factuurNummer ?? "").toLowerCase().includes(q) ||
        (r.betalerNaam ?? "").toLowerCase().includes(q)
      );
    });
  }, [rows, zoek, status]);

  const columns: Column<FactuurRow>[] = [
    { header: t("crematoriumFacturen.col.nummer"), cell: (r) => r.factuurNummer ?? "—" },
    {
      header: t("crematoriumFacturen.col.factuurdatum"),
      cell: (r) => (r.factuurDatum ? formatDate(r.factuurDatum, locale) : "—"),
    },
    {
      header: t("crematoriumFacturen.col.vervaldatum"),
      cell: (r) => (r.vervaldatum ? formatDate(r.vervaldatum, locale) : "—"),
    },
    { header: t("crematoriumFacturen.col.betaler"), cell: (r) => r.betalerNaam ?? "—" },
    {
      header: t("crematoriumFacturen.col.status"),
      cell: (r) => (
        <StatusBadge tone={tone(r.betaalStatus)}>
          {t(`crematoriumFacturen.status.${r.betaalStatus}`)}
        </StatusBadge>
      ),
    },
    {
      header: t("crematoriumFacturen.col.totaal"),
      align: "right",
      cell: (r) => (r.totaalBedrag != null ? formatEuro(r.totaalBedrag, locale) : "—"),
    },
    {
      header: t("crematoriumFacturen.col.openstaand"),
      align: "right",
      cell: (r) => (r.openstaandBedrag != null ? formatEuro(r.openstaandBedrag, locale) : "—"),
    },
  ];

  return (
    <div className="product-overview">
      <div className="asiel-tarief__panel">
        <div className="asiel-tarief__toolbar">
          <input
            type="search"
            autoComplete="off"
            className="fawkes-products-search__input"
            placeholder={t("crematoriumFacturen.search.placeholder")}
            value={zoek}
            onChange={(e) => setZoek(e.target.value)}
            aria-label={t("crematoriumFacturen.search.placeholder")}
          />
          <SelectField
            label={t("crematoriumFacturen.col.status")}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            options={[
              { value: "", label: t("crematoriumFacturen.filter.alle") },
              ...STATUSSEN.map((s) => ({ value: s, label: t(`crematoriumFacturen.status.${s}`) })),
            ]}
          />
        </div>

        {isError && (
          <div className="asiel-tarief__notice asiel-tarief__notice--error">
            {t("crematoriumFacturen.load.error")}
          </div>
        )}

        <IndeelbareTabel<FactuurRow> tabelKey="crematorium-facturen-list"
          columns={columns}
          rows={filtered}
          getRowKey={(r) => r.id}
          empty={isLoading ? t("common.loading") : t("crematoriumFacturen.empty")}
        />
      </div>
    </div>
  );
}
